// Cinematic NFS-style game lobby. Renders the selected track as an
// auto-orbiting 3D background and switches it on track change. Falls back to
// a flat neon background when models aren't available yet.

import * as THREE from 'three';
import { MP_SERVER_URL } from './config.js';
import { PRESET_TRACKS, getPreset } from './PresetTracks.js';
import { placePiece, computeTrackBounds, CELL_RAW, GRID_SCALE } from './Track.js';

const STORAGE_NAME = 'racing.playerName';
const STORAGE_ROOM = 'racing.lastRoomId';
const STORAGE_TRACK = 'racing.lastTrackId';

const HERO_VEHICLES = [
	'vehicle-truck-yellow',
	'vehicle-truck-purple',
	'vehicle-truck-red',
	'vehicle-truck-green',
];
const _balloonOffset = new THREE.Vector3();
const _balloonSide = new THREE.Vector3();
const _balloonTarget = new THREE.Vector3();
const _cameraDir = new THREE.Vector3();

function $( tag, attrs = {}, children = [] ) {

	const el = document.createElement( tag );
	for ( const [ k, v ] of Object.entries( attrs ) ) {

		if ( k === 'style' ) Object.assign( el.style, v );
		else if ( k === 'class' ) el.className = v;
		else if ( k === 'html' ) el.innerHTML = v;
		else if ( k.startsWith( 'on' ) ) el.addEventListener( k.slice( 2 ), v );
		else el.setAttribute( k, v );

	}
	for ( const c of children ) el.append( c );
	return el;

}

function injectStyles() {

	if ( document.getElementById( 'home-screen-css' ) ) return;
	const css = document.createElement( 'style' );
	css.id = 'home-screen-css';
	css.textContent = `
		@keyframes neon-pulse {
			0%, 100% { filter: drop-shadow(0 0 6px rgba(0,245,255,0.85)) drop-shadow(0 0 22px rgba(0,245,255,0.45)); }
			50%      { filter: drop-shadow(0 0 10px rgba(255,43,214,0.95)) drop-shadow(0 0 32px rgba(255,43,214,0.55)); }
		}
		@keyframes scanline {
			0%   { transform: translateY(-100%); opacity: 0; }
			10%  { opacity: 0.6; }
			90%  { opacity: 0.6; }
			100% { transform: translateY(100vh); opacity: 0; }
		}
		@keyframes glitch {
			0%, 92%, 100% { transform: translate(0,0); }
			93% { transform: translate(-2px, 1px); }
			95% { transform: translate(2px, -1px); }
			97% { transform: translate(-1px, 0); }
		}
		@keyframes float-up {
			0%   { transform: translate3d(var(--x,0), 100vh, 0) scale(0.7); opacity: 0; }
			10%  { opacity: 0.9; }
			90%  { opacity: 0.6; }
			100% { transform: translate3d(calc(var(--x,0) + var(--drift,0)), -10vh, 0) scale(1.1); opacity: 0; }
		}
		@keyframes start-pulse {
			0%, 100% { box-shadow: 0 0 20px rgba(52,255,157,0.55), 0 0 40px rgba(52,255,157,0.25), inset 0 0 18px rgba(52,255,157,0.18); }
			50%      { box-shadow: 0 0 32px rgba(52,255,157,0.95), 0 0 70px rgba(52,255,157,0.55), inset 0 0 24px rgba(52,255,157,0.3); }
		}

		#home-screen {
			position: fixed; inset: 0; z-index: 100;
			background: #050010;
			color: #d6f7ff;
			font: 500 14px 'Rajdhani', 'Segoe UI', -apple-system, sans-serif;
			overflow: hidden;
			user-select: none;
		}

		#home-screen .bg-canvas {
			position: absolute; inset: 0;
			width: 100%; height: 100%;
			z-index: 0;
		}
		#home-screen .bg-aurora {
			position: absolute; inset: -8%; z-index: 1;
			background:
				radial-gradient(circle at 18% 18%, rgba(0,245,255,0.16), transparent 28%),
				radial-gradient(circle at 82% 26%, rgba(255,43,214,0.16), transparent 30%),
				radial-gradient(circle at 50% 72%, rgba(52,255,157,0.14), transparent 24%),
				linear-gradient(180deg, rgba(14,6,36,0.2) 0%, transparent 38%, rgba(5,0,16,0.34) 100%);
			filter: blur(22px);
			mix-blend-mode: screen;
			pointer-events: none;
			opacity: 0.9;
		}
		#home-screen .bg-vignette {
			position: absolute; inset: 0; z-index: 1;
			background:
				radial-gradient(circle at 50% 42%, transparent 0%, rgba(5,0,16,0.32) 48%, rgba(5,0,16,0.76) 100%),
				linear-gradient(180deg, rgba(5,0,16,0.3) 0%, rgba(5,0,16,0.06) 28%, transparent 48%, rgba(5,0,16,0.68) 100%);
			pointer-events: none;
		}
		#home-screen .bg-grid {
			position: absolute; inset: 0; z-index: 1;
			background-image:
				linear-gradient(rgba(0,245,255,0.08) 1px, transparent 1px),
				linear-gradient(90deg, rgba(255,43,214,0.06) 1px, transparent 1px);
			background-size: 80px 80px, 80px 80px;
			mask-image: radial-gradient(circle at 50% 60%, transparent 30%, #000 80%);
			-webkit-mask-image: radial-gradient(circle at 50% 60%, transparent 30%, #000 80%);
			pointer-events: none;
			opacity: 0.62;
		}
		#home-screen .scanline {
			position: absolute; left: 0; right: 0; top: 0; height: 22vh; z-index: 2;
			background: linear-gradient(180deg, transparent, rgba(0,245,255,0.03) 40%, rgba(0,245,255,0.1) 50%, rgba(0,245,255,0.03) 60%, transparent);
			animation: scanline 12s linear infinite;
			pointer-events: none;
			mix-blend-mode: screen;
			opacity: 0.45;
		}
		#home-screen .particles { position: absolute; inset: 0; z-index: 2; pointer-events: none; overflow: hidden; }
		#home-screen .particles span {
			position: absolute; bottom: 0;
			width: 4px; height: 4px; border-radius: 50%;
			background: var(--c, #00f5ff);
			box-shadow: 0 0 8px var(--c, #00f5ff), 0 0 16px var(--c, #00f5ff);
			animation: float-up var(--dur, 9s) linear infinite;
			animation-delay: var(--delay, 0s);
		}

		#home-screen .stage {
			position: relative; z-index: 5;
			height: 100%; width: 100%;
			display: grid;
			grid-template-rows: auto 1fr auto;
			padding: 48px clamp(32px, 6vw, 96px);
			box-sizing: border-box;
		}

		#home-screen .brand-row {
			display: flex; align-items: flex-start; justify-content: space-between;
			gap: 24px;
		}
		#home-screen .brand {
			font-weight: 700;
			letter-spacing: 0.22em;
			font-size: clamp(28px, 5vw, 56px);
			line-height: 0.95;
			text-transform: uppercase;
			color: #fff;
			text-shadow:
				0 0 12px rgba(0,245,255,0.85),
				0 0 32px rgba(0,245,255,0.55),
				0 0 60px rgba(138,59,255,0.45);
			animation: glitch 6s infinite;
		}
		#home-screen .brand .accent {
			color: #ff2bd6;
			text-shadow: 0 0 12px rgba(255,43,214,0.95), 0 0 32px rgba(255,43,214,0.55);
		}
		#home-screen .brand-sub {
			margin-top: 8px;
			font-size: 12px;
			letter-spacing: 0.4em;
			text-transform: uppercase;
			color: rgba(214,247,255,0.55);
		}

		#home-screen .status {
			display: inline-flex; align-items: center; gap: 8px;
			padding: 8px 14px;
			border: 1px solid rgba(0,245,255,0.45);
			border-radius: 999px;
			background: rgba(0,245,255,0.05);
			color: #00f5ff;
			font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; font-weight: 600;
			text-shadow: 0 0 6px rgba(0,245,255,0.6);
		}
		#home-screen .status.offline { color: #ff2bd6; border-color: rgba(255,43,214,0.45); background: rgba(255,43,214,0.05); text-shadow: 0 0 6px rgba(255,43,214,0.6); }
		#home-screen .status .dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; box-shadow: 0 0 8px currentColor; animation: neon-pulse 2s infinite; }

		#home-screen .center {
			align-self: end;
			display: grid;
			gap: 18px;
			max-width: 1100px;
			width: 100%;
			margin: 0 auto;
		}
		#home-screen .section-label {
			font-size: 11px; letter-spacing: 0.42em; text-transform: uppercase;
			color: rgba(214,247,255,0.6);
			margin: 0;
		}

		#home-screen .track-rail {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
			gap: 14px;
		}
		#home-screen .track-card {
			position: relative;
			padding: 14px 16px 16px;
			border-radius: 14px;
			background: linear-gradient(180deg, rgba(20,8,40,0.65), rgba(8,4,20,0.85));
			border: 1px solid rgba(0,245,255,0.35);
			color: inherit; cursor: pointer; text-align: left;
			font: inherit;
			overflow: hidden;
			transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
		}
		#home-screen .track-card::before {
			content: ''; position: absolute; inset: 0;
			background: radial-gradient(circle at 80% 0%, rgba(0,245,255,0.18), transparent 55%);
			opacity: 0.7; pointer-events: none;
		}
		#home-screen .track-card .num {
			position: absolute; top: 10px; right: 14px;
			font-size: 11px; letter-spacing: 0.22em;
			color: rgba(214,247,255,0.45);
			font-weight: 600;
		}
		#home-screen .track-card .t-name {
			font-weight: 700; font-size: 18px; letter-spacing: 0.04em;
			text-transform: uppercase;
			color: #fff;
			text-shadow: 0 0 6px rgba(0,245,255,0.4);
		}
		#home-screen .track-card .t-hint {
			font-size: 12px; letter-spacing: 0.06em;
			color: rgba(214,247,255,0.65);
			margin-top: 4px;
		}
		#home-screen .track-card:hover {
			transform: translateY(-3px);
			border-color: #00f5ff;
			box-shadow: 0 14px 36px rgba(0,245,255,0.25);
		}
		#home-screen .track-card.selected {
			border-color: #ff2bd6;
			background: linear-gradient(180deg, rgba(60,10,80,0.7), rgba(20,4,40,0.9));
			box-shadow: 0 0 28px rgba(255,43,214,0.45), inset 0 0 30px rgba(255,43,214,0.15);
		}
		#home-screen .track-card.selected::after {
			content: ''; position: absolute; left: 14px; bottom: 12px; right: 14px;
			height: 2px; background: linear-gradient(90deg, transparent, #ff2bd6 30%, #00f5ff 70%, transparent);
			box-shadow: 0 0 10px rgba(255,43,214,0.8);
		}

		#home-screen .field-row {
			display: grid; grid-template-columns: minmax(220px, 1.2fr) minmax(220px, 1.2fr) auto;
			gap: 14px; align-items: end;
		}
		#home-screen .field { display: flex; flex-direction: column; gap: 6px; }
		#home-screen .field input {
			padding: 12px 16px;
			background: rgba(8,4,20,0.6);
			border: 1px solid rgba(0,245,255,0.4);
			border-radius: 12px;
			color: #fff;
			font: 600 15px 'Rajdhani', sans-serif;
			letter-spacing: 0.06em;
			outline: none;
			transition: border-color 0.18s ease, box-shadow 0.18s ease;
		}
		#home-screen .field input::placeholder { color: rgba(214,247,255,0.35); }
		#home-screen .field input:focus {
			border-color: #00f5ff;
			box-shadow: 0 0 0 3px rgba(0,245,255,0.15), 0 0 18px rgba(0,245,255,0.35);
		}

		#home-screen .ghost-btn {
			padding: 12px 18px;
			background: rgba(8,4,20,0.6);
			border: 1px solid rgba(0,245,255,0.4);
			color: #00f5ff;
			border-radius: 12px;
			font: 700 12px 'Rajdhani', sans-serif;
			letter-spacing: 0.22em;
			text-transform: uppercase;
			cursor: pointer;
			transition: all 0.15s ease;
			text-shadow: 0 0 6px rgba(0,245,255,0.5);
		}
		#home-screen .ghost-btn:hover {
			background: rgba(0,245,255,0.12);
			border-color: #fff;
			color: #fff;
		}

		#home-screen .cta-row {
			display: grid;
			grid-template-columns: 1.4fr 1fr;
			gap: 16px;
			margin-top: 8px;
		}
		#home-screen .cta {
			padding: 18px 26px;
			border-radius: 14px;
			border: 1px solid transparent;
			font: 700 16px 'Rajdhani', sans-serif;
			letter-spacing: 0.34em;
			text-transform: uppercase;
			cursor: pointer;
			transition: all 0.18s ease;
			background: rgba(8,4,20,0.6);
			color: #00f5ff;
			text-shadow: 0 0 6px rgba(0,245,255,0.6);
		}
		#home-screen .cta-multi {
			background: linear-gradient(135deg, #34ff9d, #00f5ff);
			color: #050010;
			border-color: #34ff9d;
			animation: start-pulse 2.4s infinite;
			text-shadow: none;
		}
		#home-screen .cta-multi:hover { transform: translateY(-2px) scale(1.01); }
		#home-screen .cta-multi:disabled {
			background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.35);
			border-color: rgba(255,255,255,0.12);
			animation: none; cursor: not-allowed;
			text-shadow: none;
		}
		#home-screen .cta-single {
			border-color: rgba(255,43,214,0.5);
		}
		#home-screen .cta-single:hover {
			border-color: #ff2bd6; color: #ff2bd6;
			text-shadow: 0 0 6px rgba(255,43,214,0.7);
			box-shadow: 0 0 22px rgba(255,43,214,0.3);
		}

		#home-screen .footer {
			display: flex; justify-content: space-between; align-items: flex-end;
			gap: 16px;
			color: rgba(214,247,255,0.55);
			font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase;
		}
		#home-screen .controls-bar {
			display: flex; gap: 14px; flex-wrap: wrap;
			padding: 10px 16px;
			background: rgba(8,4,20,0.55);
			border: 1px solid rgba(0,245,255,0.25);
			border-radius: 12px;
			backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
		}
		#home-screen .controls-bar .key {
			display: inline-block; padding: 2px 8px; border-radius: 6px;
			background: rgba(0,245,255,0.08);
			border: 1px solid rgba(0,245,255,0.4);
			color: #00f5ff;
			font-family: ui-monospace, monospace; font-size: 11px;
			margin-right: 6px;
			text-shadow: 0 0 4px rgba(0,245,255,0.6);
		}
		#home-screen .err { color: #ff6e6e; font-size: 12px; min-height: 16px; letter-spacing: 0.1em; }

		@media (min-aspect-ratio: 16 / 9) {
			#home-screen .stage {
				padding-top: clamp(28px, 3vh, 42px);
				padding-bottom: clamp(22px, 3vh, 34px);
			}
			#home-screen .center {
				max-width: min(1040px, 62vw);
			}
		}
	`;
	document.head.appendChild( css );

}

function genRoomId() {

	const adj = [ 'fast', 'wild', 'loud', 'neon', 'turbo', 'drift', 'blaze', 'grit' ];
	const noun = [ 'pit', 'lap', 'box', 'kart', 'dash', 'rev', 'apex', 'gear' ];
	const a = adj[ Math.floor( Math.random() * adj.length ) ];
	const n = noun[ Math.floor( Math.random() * noun.length ) ];
	return `${ a }-${ n }-${ Math.floor( Math.random() * 90 + 10 ) }`;

}

function loadName( fallback ) {

	return ( fallback || localStorage.getItem( STORAGE_NAME ) || `racer-${ Math.floor( Math.random() * 999 ) }` ).slice( 0, 24 );

}

function buildLoopPath( cells ) {

	const trackCells = cells.filter( ( [ , , type ] ) => type.startsWith( 'track-' ) );
	if ( trackCells.length < 2 ) return null;

	const byKey = new Map( trackCells.map( ( cell ) => [ `${ cell[ 0 ] },${ cell[ 1 ] }`, cell ] ) );
	const headingByOrient = { 0: [ 0, 1 ], 10: [ 0, -1 ], 16: [ 1, 0 ], 22: [ -1, 0 ] };

	function neighborsOf( cell ) {

		const [ gx, gz ] = cell;
		const neighbors = [];
		for ( const [ dx, dz ] of [ [ 1, 0 ], [ -1, 0 ], [ 0, 1 ], [ 0, -1 ] ] ) {

			const next = byKey.get( `${ gx + dx },${ gz + dz }` );
			if ( next ) neighbors.push( next );

		}
		return neighbors;

	}

	function centerOf( cell ) {

		return {
			x: ( cell[ 0 ] + 0.5 ) * CELL_RAW * GRID_SCALE,
			z: ( cell[ 1 ] + 0.5 ) * CELL_RAW * GRID_SCALE,
		};

	}

	const start = trackCells.find( ( [ , , type ] ) => type === 'track-finish' ) || trackCells[ 0 ];
	const startNeighbors = neighborsOf( start );
	if ( startNeighbors.length === 0 ) return trackCells.map( centerOf );

	const [ fdx = 0, fdz = 1 ] = headingByOrient[ start[ 3 ] ] || [ 0, 1 ];
	let current = startNeighbors[ 0 ];
	let bestDot = - Infinity;
	for ( const neighbor of startNeighbors ) {

		const dx = neighbor[ 0 ] - start[ 0 ];
		const dz = neighbor[ 1 ] - start[ 1 ];
		const dot = dx * fdx + dz * fdz;
		if ( dot > bestDot ) {

			bestDot = dot;
			current = neighbor;

		}

	}

	const ordered = [ centerOf( start ) ];
	const visited = new Set( [ `${ start[ 0 ] },${ start[ 1 ] }` ] );
	let previous = start;

	while ( current ) {

		const key = `${ current[ 0 ] },${ current[ 1 ] }`;
		if ( key === `${ start[ 0 ] },${ start[ 1 ] }` ) break;
		if ( visited.has( key ) ) break;

		ordered.push( centerOf( current ) );
		visited.add( key );

		const next = neighborsOf( current ).find(
			( neighbor ) => neighbor[ 0 ] !== previous[ 0 ] || neighbor[ 1 ] !== previous[ 1 ]
		);
		previous = current;
		current = next || null;

	}

	return ordered;

}

function makeBalloonBannerTexture() {

	const canvas = document.createElement( 'canvas' );
	canvas.width = 768;
	canvas.height = 192;
	const ctx = canvas.getContext( '2d' );

	const grad = ctx.createLinearGradient( 0, 0, canvas.width, 0 );
	grad.addColorStop( 0, '#ff2bd6' );
	grad.addColorStop( 0.5, '#ff8a48' );
	grad.addColorStop( 1, '#00f5ff' );
	ctx.fillStyle = grad;
	ctx.fillRect( 0, 0, canvas.width, canvas.height );

	ctx.fillStyle = 'rgba(5, 0, 16, 0.22)';
	for ( let x = - canvas.height; x < canvas.width + canvas.height; x += 48 ) {

		ctx.save();
		ctx.translate( x, 0 );
		ctx.rotate( Math.PI / 5 );
		ctx.fillRect( 0, - canvas.height, 18, canvas.height * 3 );
		ctx.restore();

	}

	ctx.strokeStyle = 'rgba(255,255,255,0.72)';
	ctx.lineWidth = 8;
	ctx.strokeRect( 12, 12, canvas.width - 24, canvas.height - 24 );

	ctx.fillStyle = '#ffffff';
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	ctx.font = '700 72px Rajdhani, Segoe UI, sans-serif';
	ctx.shadowColor = 'rgba(0,245,255,0.65)';
	ctx.shadowBlur = 22;
	ctx.fillText( 'BUILT BY LOKI', canvas.width / 2, canvas.height / 2 );

	const tex = new THREE.CanvasTexture( canvas );
	tex.colorSpace = THREE.SRGBColorSpace;
	return tex;

}

function makeGlowSpriteTexture() {

	const canvas = document.createElement( 'canvas' );
	canvas.width = canvas.height = 256;
	const ctx = canvas.getContext( '2d' );

	const grad = ctx.createRadialGradient( 128, 128, 0, 128, 128, 128 );
	grad.addColorStop( 0, 'rgba(255,255,255,0.95)' );
	grad.addColorStop( 0.22, 'rgba(255,255,255,0.52)' );
	grad.addColorStop( 0.58, 'rgba(255,255,255,0.14)' );
	grad.addColorStop( 1, 'rgba(255,255,255,0)' );
	ctx.fillStyle = grad;
	ctx.fillRect( 0, 0, canvas.width, canvas.height );

	const tex = new THREE.CanvasTexture( canvas );
	tex.colorSpace = THREE.SRGBColorSpace;
	return tex;

}

function makeBeamTexture() {

	const canvas = document.createElement( 'canvas' );
	canvas.width = 96;
	canvas.height = 512;
	const ctx = canvas.getContext( '2d' );

	const grad = ctx.createLinearGradient( 0, 0, 0, canvas.height );
	grad.addColorStop( 0, 'rgba(255,255,255,0)' );
	grad.addColorStop( 0.18, 'rgba(255,255,255,0.14)' );
	grad.addColorStop( 0.5, 'rgba(255,255,255,0.95)' );
	grad.addColorStop( 0.82, 'rgba(255,255,255,0.16)' );
	grad.addColorStop( 1, 'rgba(255,255,255,0)' );
	ctx.fillStyle = grad;
	ctx.fillRect( 0, 0, canvas.width, canvas.height );

	const tex = new THREE.CanvasTexture( canvas );
	tex.colorSpace = THREE.SRGBColorSpace;
	return tex;

}

function createBalloonRig( bannerTexture ) {

	const balloon = new THREE.Group();

	const shell = new THREE.Mesh(
		new THREE.SphereGeometry( 2.25, 28, 24 ),
		new THREE.MeshStandardMaterial( {
			color: 0xff8a48,
			emissive: 0x501018,
			emissiveIntensity: 0.6,
			roughness: 0.45,
			metalness: 0.05,
		} )
	);
	shell.scale.set( 1, 1.18, 1 );
	balloon.add( shell );

	const stripe = new THREE.Mesh(
		new THREE.TorusGeometry( 1.72, 0.2, 18, 48 ),
		new THREE.MeshStandardMaterial( {
			color: 0xf8f8ff,
			emissive: 0x3a3a60,
			emissiveIntensity: 0.25,
			roughness: 0.4,
			metalness: 0.15,
		} )
	);
	stripe.rotation.x = Math.PI / 2;
	stripe.position.y = - 0.2;
	balloon.add( stripe );

	const basket = new THREE.Mesh(
		new THREE.BoxGeometry( 0.95, 0.5, 0.75 ),
		new THREE.MeshStandardMaterial( {
			color: 0x4a2c1e,
			emissive: 0x180c06,
			roughness: 0.8,
			metalness: 0.05,
		} )
	);
	basket.position.y = - 4.35;
	balloon.add( basket );

	const ropePoints = [
		new THREE.Vector3( -1.15, -1.75, -0.7 ), new THREE.Vector3( -0.35, - 4.05, -0.28 ),
		new THREE.Vector3( 1.15, -1.75, -0.7 ), new THREE.Vector3( 0.35, - 4.05, -0.28 ),
		new THREE.Vector3( -1.15, -1.75, 0.7 ), new THREE.Vector3( -0.35, - 4.05, 0.28 ),
		new THREE.Vector3( 1.15, -1.75, 0.7 ), new THREE.Vector3( 0.35, - 4.05, 0.28 ),
	];
	const ropes = new THREE.LineSegments(
		new THREE.BufferGeometry().setFromPoints( ropePoints ),
		new THREE.LineBasicMaterial( { color: 0xfff4d4, transparent: true, opacity: 0.82 } )
	);
	balloon.add( ropes );

	const banner = new THREE.Mesh(
		new THREE.PlaneGeometry( 6.5, 1.6 ),
		new THREE.MeshBasicMaterial( {
			map: bannerTexture,
			transparent: true,
			side: THREE.DoubleSide,
		} )
	);
	banner.position.set( 0, - 0.55, - 5.1 );
	banner.rotation.x = - 0.06;
	balloon.add( banner );

	return { balloon, banner };

}

function createTracksideFigure( {
	bodyColor = 0xffffff,
	accentColor = 0xff2bd6,
	pose = 0,
	scale = 1,
} = {} ) {

	const group = new THREE.Group();

	const body = new THREE.Mesh(
		new THREE.BoxGeometry( 0.45, 0.75, 0.22 ),
		new THREE.MeshStandardMaterial( {
			color: bodyColor,
			emissive: 0x120814,
			emissiveIntensity: 0.12,
			roughness: 0.82,
			metalness: 0.06,
		} )
	);
	body.position.y = 0.75;
	group.add( body );

	const skirt = new THREE.Mesh(
		new THREE.CylinderGeometry( 0.32, 0.22, 0.24, 6 ),
		new THREE.MeshStandardMaterial( {
			color: accentColor,
			emissive: 0x180618,
			emissiveIntensity: 0.18,
			roughness: 0.62,
			metalness: 0.08,
		} )
	);
	skirt.position.y = 0.35;
	group.add( skirt );

	const head = new THREE.Mesh(
		new THREE.SphereGeometry( 0.2, 12, 10 ),
		new THREE.MeshStandardMaterial( {
			color: 0xf5c5a3,
			roughness: 0.95,
			metalness: 0.02,
		} )
	);
	head.position.y = 1.28;
	group.add( head );

	const hair = new THREE.Mesh(
		new THREE.SphereGeometry( 0.21, 12, 10, 0, Math.PI * 2, 0, Math.PI * 0.62 ),
		new THREE.MeshStandardMaterial( {
			color: pose % 2 === 0 ? 0x5a2a18 : 0xf2d58f,
			roughness: 0.88,
			metalness: 0.03,
		} )
	);
	hair.position.y = 1.33;
	group.add( hair );

	for ( const side of [ - 1, 1 ] ) {

		const arm = new THREE.Mesh(
			new THREE.BoxGeometry( 0.12, 0.48, 0.12 ),
			new THREE.MeshStandardMaterial( {
				color: 0xf5c5a3,
				roughness: 0.92,
				metalness: 0.02,
			} )
		);
		arm.position.set( side * 0.33, 0.86, 0 );
		arm.rotation.z = side * ( 0.9 + pose * 0.12 );
		group.add( arm );

		const pom = new THREE.Mesh(
			new THREE.SphereGeometry( 0.14, 10, 8 ),
			new THREE.MeshStandardMaterial( {
				color: side < 0 ? accentColor : 0x00d9ff,
				emissive: side < 0 ? 0x22081a : 0x082028,
				emissiveIntensity: 0.25,
				roughness: 0.45,
				metalness: 0.18,
			} )
		);
		pom.position.set( side * 0.53, 1.16 + pose * 0.05, 0 );
		group.add( pom );

		const leg = new THREE.Mesh(
			new THREE.BoxGeometry( 0.1, 0.48, 0.1 ),
			new THREE.MeshStandardMaterial( {
				color: 0xf5c5a3,
				roughness: 0.9,
				metalness: 0.02,
			} )
		);
		leg.position.set( side * 0.12, 0.02, 0 );
		leg.rotation.z = side * 0.08;
		group.add( leg );

	}

	group.scale.setScalar( scale );
	return group;

}

function createCrowdStand( { width = 18, tiers = 4, columns = 9 } = {} ) {

	const group = new THREE.Group();

	const baseMat = new THREE.MeshStandardMaterial( {
		color: 0x3d4760,
		emissive: 0x0d1224,
		emissiveIntensity: 0.18,
		roughness: 0.9,
		metalness: 0.08,
	} );
	const seatMat = new THREE.MeshStandardMaterial( {
		color: 0x69758d,
		emissive: 0x141a2e,
		emissiveIntensity: 0.08,
		roughness: 0.92,
		metalness: 0.04,
	} );

	for ( let tier = 0; tier < tiers; tier ++ ) {

		const depth = 3.8 - tier * 0.55;
		const riser = new THREE.Mesh( new THREE.BoxGeometry( width, 0.42, depth ), tier === 0 ? baseMat : seatMat );
		riser.position.set( 0, tier * 0.46, - tier * 1.08 );
		group.add( riser );

	}

	const crowdColors = [ 0xff5e7a, 0x00d9ff, 0xf9f871, 0x7dff9f, 0xffffff, 0xffb347 ];
	for ( let row = 0; row < tiers; row ++ ) {

		for ( let col = 0; col < columns; col ++ ) {

			const spectator = new THREE.Group();
			const body = new THREE.Mesh(
				new THREE.BoxGeometry( 0.3, 0.45, 0.18 ),
				new THREE.MeshStandardMaterial( {
					color: crowdColors[ ( row * columns + col ) % crowdColors.length ],
					emissive: 0x101018,
					emissiveIntensity: 0.08,
					roughness: 0.86,
					metalness: 0.04,
				} )
			);
			body.position.y = 0.38;
			spectator.add( body );

			const head = new THREE.Mesh(
				new THREE.SphereGeometry( 0.12, 10, 8 ),
				new THREE.MeshStandardMaterial( {
					color: col % 3 === 0 ? 0xf5c5a3 : col % 3 === 1 ? 0xd8926a : 0x8a5638,
					roughness: 0.95,
					metalness: 0.02,
				} )
			);
			head.position.y = 0.71;
			spectator.add( head );

			spectator.position.set(
				( col / Math.max( columns - 1, 1 ) - 0.5 ) * ( width - 2.2 ),
				row * 0.46 + 0.18,
				0.85 - row * 1.08
			);
			spectator.rotation.y = ( col / columns - 0.5 ) * 0.35;
			group.add( spectator );

		}

	}

	const rail = new THREE.Mesh(
		new THREE.BoxGeometry( width + 0.8, 0.12, 0.12 ),
		new THREE.MeshStandardMaterial( {
			color: 0x9cc8dd,
			emissive: 0x0d2430,
			emissiveIntensity: 0.18,
			roughness: 0.54,
			metalness: 0.26,
		} )
	);
	rail.position.set( 0, 1.95, 1.55 );
	group.add( rail );

	return group;

}

function addPreviewCrowd( target, bounds ) {

	const centerX = bounds.centerX / GRID_SCALE;
	const centerZ = bounds.centerZ / GRID_SCALE;
	const halfWidth = bounds.halfWidth / GRID_SCALE;
	const halfDepth = bounds.halfDepth / GRID_SCALE;

	const stands = [
		{ x: centerX - halfWidth * 0.32, z: centerZ - halfDepth - 9, rotY: 0.08, width: 18 },
		{ x: centerX + halfWidth + 8, z: centerZ + halfDepth * 0.06, rotY: - Math.PI / 2 + 0.12, width: 16 },
		{ x: centerX - halfWidth - 7, z: centerZ + halfDepth * 0.34, rotY: Math.PI / 2 - 0.08, width: 16 },
	];

	for ( const spec of stands ) {

		const stand = createCrowdStand( { width: spec.width } );
		stand.position.set( spec.x, 0.35, spec.z );
		stand.rotation.y = spec.rotY;
		target.add( stand );

	}

	const cheerBaseZ = centerZ + halfDepth + 3.8;
	for ( let i = 0; i < 4; i ++ ) {

		const figure = createTracksideFigure( {
			bodyColor: i % 2 === 0 ? 0xffffff : 0xffe4ee,
			accentColor: i % 2 === 0 ? 0xff4faf : 0x6cf3ff,
			pose: i,
			scale: 0.9,
		} );
		figure.position.set( centerX - 3 + i * 2.1, 0.2, cheerBaseZ );
		figure.rotation.y = Math.PI;
		target.add( figure );

	}

}

// ─── Cinematic 3D background ────────────────────────────────────────────

function createBackgroundScene( models, canvas ) {

	const renderer = new THREE.WebGLRenderer( {
		canvas, antialias: true, alpha: true, powerPreference: 'low-power'
	} );
	renderer.setPixelRatio( Math.min( window.devicePixelRatio, 2 ) );
	renderer.setSize( window.innerWidth, window.innerHeight, false );
	renderer.shadowMap.enabled = false;
	renderer.toneMapping = THREE.ACESFilmicToneMapping;
	renderer.toneMappingExposure = 1.22;

	const scene = new THREE.Scene();
	scene.background = null;
	scene.fog = new THREE.FogExp2( 0x100630, 0.012 );
	const balloonBannerTexture = makeBalloonBannerTexture();
	const glowTexture = makeGlowSpriteTexture();
	const beamTexture = makeBeamTexture();

	// Bright cyan-sky/magenta-floor hemisphere for full-spectrum rim color
	scene.add( new THREE.HemisphereLight( 0x8ceeff, 0x7f3aa8, 1.05 ) );
	scene.add( new THREE.AmbientLight( 0x35204d, 0.55 ) );

	// Two strong key lights from opposing colors
	const cyanLight = new THREE.DirectionalLight( 0x00f5ff, 1.45 );
	cyanLight.position.set( 22, 24, 12 );
	scene.add( cyanLight );

	const magentaLight = new THREE.DirectionalLight( 0xff2bd6, 1.2 );
	magentaLight.position.set( -22, 20, -16 );
	scene.add( magentaLight );

	const goldLight = new THREE.DirectionalLight( 0xffd45a, 0.9 );
	goldLight.position.set( 0, 14, -28 );
	scene.add( goldLight );

	// Disco-style orbiting point lights painted across the track surface
	const discoColors = [ 0x00f5ff, 0xff2bd6, 0xffd45a ];
	const discoLights = discoColors.map( ( c, i ) => {

		const l = new THREE.PointLight( c, 8, 42, 1.6 );
		l.userData = { phase: ( i / discoColors.length ) * Math.PI * 2, speed: 0.28 + i * 0.05, radius: 16 + i * 2 };
		scene.add( l );
		return l;

	} );

	const previewRoot = new THREE.Group();
	previewRoot.rotation.x = 0.24;
	previewRoot.position.y = - 2.8;
	scene.add( previewRoot );

	// Glowing ground with vibrant radial gradient
	const groundTex = makeNeonGroundTexture();
	const ground = new THREE.Mesh(
		new THREE.PlaneGeometry( 320, 320 ),
		new THREE.MeshBasicMaterial( { map: groundTex, transparent: true, opacity: 0.72, depthWrite: false } )
	);
	ground.rotation.x = - Math.PI / 2;
	ground.position.y = - 0.6;
	previewRoot.add( ground );

	// Sweeping neon floor bars, intentionally subtle to avoid distracting motion.
	const laserGroup = new THREE.Group();
	const laserGeom = new THREE.PlaneGeometry( 200, 0.45 );
	const laserColors = [ 0x00f5ff, 0xff2bd6 ];
	const lasers = laserColors.map( ( c, i ) => {

		const mat = new THREE.MeshBasicMaterial( {
			color: c, transparent: true, opacity: 0.08,
			blending: THREE.AdditiveBlending, depthWrite: false,
		} );
		const mesh = new THREE.Mesh( laserGeom, mat );
		mesh.rotation.x = - Math.PI / 2;
		mesh.rotation.z = ( i / laserColors.length ) * Math.PI;
		mesh.position.y = - 0.55;
		mesh.userData = { phase: i * 1.7 };
		laserGroup.add( mesh );
		return mesh;

	} );
	previewRoot.add( laserGroup );

	// Big soft glows and vertical beams keep the upper frame alive on wide screens.
	const skyGroup = new THREE.Group();
	previewRoot.add( skyGroup );

	const skyGlowSpecs = [
		{ color: 0xff2bd6, scale: [ 92, 38 ], position: [ 0, 26, - 74 ], opacity: 0.11 },
		{ color: 0x00f5ff, scale: [ 70, 28 ], position: [ - 30, 20, - 48 ], opacity: 0.09 },
		{ color: 0xffd45a, scale: [ 16, 16 ], position: [ 42, 34, - 58 ], opacity: 0.1 },
	];
	for ( const spec of skyGlowSpecs ) {

		const glow = new THREE.Sprite( new THREE.SpriteMaterial( {
			map: glowTexture,
			color: spec.color,
			transparent: true,
			opacity: spec.opacity,
			blending: THREE.AdditiveBlending,
			depthWrite: false,
		} ) );
		glow.scale.set( spec.scale[ 0 ], spec.scale[ 1 ], 1 );
		glow.position.set( spec.position[ 0 ], spec.position[ 1 ], spec.position[ 2 ] );
		skyGroup.add( glow );

	}

	const beamGroup = new THREE.Group();
	const beamSpecs = [
		{ color: 0x00f5ff, x: - 26, z: - 18, y: 17, scaleX: 12, scaleY: 58, rotZ: 0.18, phase: 0.3 },
		{ color: 0xff2bd6, x: 24, z: - 12, y: 18, scaleX: 14, scaleY: 62, rotZ: - 0.14, phase: 1.1 },
	];
	const beams = beamSpecs.map( ( spec ) => {

		const beam = new THREE.Sprite( new THREE.SpriteMaterial( {
			map: beamTexture,
			color: spec.color,
			transparent: true,
			opacity: 0.08,
			blending: THREE.AdditiveBlending,
			depthWrite: false,
		} ) );
		beam.position.set( spec.x, spec.y, spec.z );
		beam.scale.set( spec.scaleX, spec.scaleY, 1 );
		beam.material.rotation = spec.rotZ;
		beam.userData = spec;
		beamGroup.add( beam );
		return beam;

	} );
	previewRoot.add( beamGroup );

	const camera = new THREE.PerspectiveCamera( 42, window.innerWidth / window.innerHeight, 0.1, 900 );
	camera.position.set( 26, 13, 36 );
	camera.lookAt( 0, 3.4, 0 );

	const trackGroup = new THREE.Group();
	trackGroup.scale.setScalar( GRID_SCALE );
	previewRoot.add( trackGroup );

	const heroGroup = new THREE.Group();
	previewRoot.add( heroGroup );

	const state = {
		renderer, scene, camera, trackGroup, heroGroup, previewRoot,
		bounds: { centerX: 0, centerZ: 0, halfWidth: 20, halfDepth: 20 },
		raf: 0,
		t0: performance.now(),
		lastFrame: performance.now(),
		hero: null,
		balloon: null,
		balloonBanner: null,
		targetSpan: 20,
		currentSpan: 20,
		targetPreviewScale: 1,
		currentPreviewScale: 1,
		targetRadius: 36,
		currentRadius: 36,
		targetCenter: new THREE.Vector3(),
		currentCenter: new THREE.Vector3(),
		wideScreenMix: 0,
	};

	function updateFraming() {

		const aspect = window.innerWidth / window.innerHeight;
		const wideMix = THREE.MathUtils.clamp( ( aspect - 1.65 ) / 0.95, 0, 1 );
		state.wideScreenMix = wideMix;
		previewRoot.rotation.x = 0.24 - wideMix * 0.05;
		previewRoot.position.y = - 2.8 + wideMix * 1.6;

	}

	function clearGroup( g ) {

		while ( g.children.length ) g.remove( g.children[ 0 ] );

	}

	function setTrack( cells, heroVehicleKey ) {

		clearGroup( trackGroup );
		clearGroup( heroGroup );
		state.hero = null;
		state.balloon = null;
		state.balloonBanner = null;
		state.path = null;
		state.pathT = 0;

		if ( ! models ) return;

		for ( const [ gx, gz, type, orient ] of cells ) {

			const piece = placePiece( models, type, gx, gz, orient );
			if ( piece ) trackGroup.add( piece );

		}

		const bounds = computeTrackBounds( cells );
		addPreviewCrowd( trackGroup, bounds );
		const span = Math.max( bounds.halfWidth, bounds.halfDepth );
		const previewScale = THREE.MathUtils.clamp( 56 / span, 0.52, 1 );
		const previewSpan = span * previewScale;
		state.bounds = bounds;
		state.targetSpan = span;
		state.targetPreviewScale = previewScale;
		state.targetCenter.set( bounds.centerX, 0, bounds.centerZ );
		state.targetRadius = previewSpan * 1.14 + 24;

		// Build a closed loop of world-space cell centers so the hero car can
		// drive credits-scene laps while the menu is open.
		const path = buildLoopPath( cells );
		state.path = path;

		// Spawn hero at the finish (or first path point), orient to first tangent.
		const finish = cells.find( ( c ) => c[ 2 ] === 'track-finish' ) || cells[ 0 ];
		const fx = ( finish[ 0 ] + 0.5 ) * CELL_RAW * GRID_SCALE;
		const fz = ( finish[ 1 ] + 0.5 ) * CELL_RAW * GRID_SCALE;

		const heroSrc = models[ heroVehicleKey ];
		if ( heroSrc ) {

			const hero = heroSrc.clone( true );
			hero.scale.setScalar( 0.5 );
			hero.position.set( fx, 0, fz );
			hero.traverse( ( c ) => { if ( c.isMesh ) { c.castShadow = false; c.receiveShadow = false; } } );
			heroGroup.add( hero );
			state.hero = hero;

			const balloonRig = createBalloonRig( balloonBannerTexture );
			heroGroup.add( balloonRig.balloon );
			state.balloon = balloonRig.balloon;
			state.balloonBanner = balloonRig.banner;

			// Align starting index of path with the finish cell so the car begins there
			if ( path && path.length > 1 ) {

				let bestIdx = 0;
				let bestDist = Infinity;
				for ( let i = 0; i < path.length; i ++ ) {

					const dx = path[ i ].x - fx;
					const dz = path[ i ].z - fz;
					const d = dx * dx + dz * dz;
					if ( d < bestDist ) { bestDist = d; bestIdx = i; }

				}
				state.pathT = bestIdx;

			}

		}

	}

	function onResize() {

		camera.aspect = window.innerWidth / window.innerHeight;
		camera.updateProjectionMatrix();
		renderer.setSize( window.innerWidth, window.innerHeight, false );
		updateFraming();

	}
	updateFraming();
	window.addEventListener( 'resize', onResize );
	state.dispose = () => {

		cancelAnimationFrame( state.raf );
		window.removeEventListener( 'resize', onResize );
		renderer.dispose();
		groundTex.dispose();
		balloonBannerTexture.dispose();
		glowTexture.dispose();
		beamTexture.dispose();

	};

	function animate() {

		const now = performance.now();
		const t = ( now - state.t0 ) / 1000;
		const dt = Math.min( ( now - state.lastFrame ) / 1000, 0.05 );
		state.lastFrame = now;

		// Ease camera radius/center toward target on track switch
		state.currentSpan += ( state.targetSpan - state.currentSpan ) * 0.06;
		state.currentPreviewScale += ( state.targetPreviewScale - state.currentPreviewScale ) * 0.06;
		state.currentRadius += ( state.targetRadius - state.currentRadius ) * 0.06;
		state.currentCenter.lerp( state.targetCenter, 0.08 );

		const previewScale = state.currentPreviewScale;
		const span = state.currentSpan * previewScale;
		scene.fog.density = THREE.MathUtils.clamp( 0.34 / ( span + 12 ), 0.0035, 0.011 );
		trackGroup.scale.setScalar( GRID_SCALE * previewScale );
		trackGroup.position.set(
			state.currentCenter.x * ( 1 - previewScale ),
			0,
			state.currentCenter.z * ( 1 - previewScale ),
		);

		const angle = 0.5 + Math.sin( t * 0.18 ) * 0.58;
		const wideMix = state.wideScreenMix;
		const r = state.currentRadius * ( 0.78 - wideMix * 0.06 );
		const cameraHeight = Math.max( 17.5 + wideMix * 3.4, span * ( 0.44 + wideMix * 0.04 ) );
		const lookHeight = 3.2 + Math.min( span * 0.055, 5.2 ) + wideMix * 1.1;
		const lookLead = Math.min( span * 0.09, 7 ) + wideMix * 1.4;
		const zLead = 4 + Math.min( span * 0.11, 9 ) + wideMix * 3.4;
		camera.position.set(
			state.currentCenter.x + Math.cos( angle ) * r * 1.03,
			cameraHeight + Math.sin( t * 0.32 ) * 0.45,
			state.currentCenter.z + Math.sin( angle ) * r + zLead
		);
		camera.lookAt(
			state.currentCenter.x,
			lookHeight,
			state.currentCenter.z + lookLead
		);

		// Drive the hero kart around the looped path (credits-scene cruise)
		if ( state.hero && state.path && state.path.length > 1 ) {

			const path = state.path;
			state.pathT = ( state.pathT + dt * 2.6 ) % path.length;
			const i0 = Math.floor( state.pathT );
			const i1 = ( i0 + 1 ) % path.length;
			const i2 = ( i0 + 2 ) % path.length;
			const f = state.pathT - i0;

			const p0 = path[ i0 ];
			const p1 = path[ i1 ];
			const hx = p0.x + ( p1.x - p0.x ) * f;
			const hz = p0.z + ( p1.z - p0.z ) * f;
			const scaledHx = state.currentCenter.x + ( hx - state.currentCenter.x ) * previewScale;
			const scaledHz = state.currentCenter.z + ( hz - state.currentCenter.z ) * previewScale;
			state.hero.position.set(
				scaledHx,
				0.05,
				scaledHz
			);
			state.hero.scale.setScalar( 0.5 * previewScale );

			// Tangent — smooth heading by averaging current and next segment
			const dxA = p1.x - p0.x, dzA = p1.z - p0.z;
			const p2 = path[ i2 ];
			const dxB = p2.x - p1.x, dzB = p2.z - p1.z;
			const tDx = dxA + ( dxB - dxA ) * f;
			const tDz = dzA + ( dzB - dzA ) * f;
			const targetY = Math.atan2( tDx, tDz );
			let cur = state.hero.rotation.y;
			let diff = targetY - cur;
			while ( diff > Math.PI ) diff -= Math.PI * 2;
			while ( diff < - Math.PI ) diff += Math.PI * 2;
			state.hero.rotation.y = cur + diff * 0.18;

		} else if ( state.hero ) {

			state.hero.rotation.y += 0.012;

		}

		if ( state.balloon ) {

			_cameraDir.subVectors( camera.position, state.currentCenter ).setY( 0 );
			if ( _cameraDir.lengthSq() > 0.0001 ) _cameraDir.normalize();
			_balloonSide.set( - _cameraDir.z, 0, _cameraDir.x );
			_balloonOffset.copy( _cameraDir ).multiplyScalar( state.currentRadius * ( 0.42 + wideMix * 0.04 ) );
			_balloonOffset.addScaledVector( _balloonSide, state.currentRadius * 0.24 );
			_balloonOffset.y = 13.8 + wideMix * 2.8 + Math.sin( t * 1.2 ) * 0.35;
			state.balloon.position.set(
				state.currentCenter.x + _balloonOffset.x,
				_balloonOffset.y,
				state.currentCenter.z + _balloonOffset.z
			);
			state.balloon.rotation.y = Math.atan2( _cameraDir.x, _cameraDir.z ) * 0.2;
			state.balloon.rotation.z = Math.sin( t * 1.4 ) * 0.08;
			if ( state.balloonBanner ) {

				state.balloonBanner.lookAt( camera.position );

			}

		}

		// Orbit the disco point lights around the track at varying heights
		const cx = state.currentCenter.x;
		const cz = state.currentCenter.z;
		for ( const l of discoLights ) {

			const d = l.userData;
			const a = d.phase + t * d.speed;
			_balloonTarget.set(
				cx + Math.cos( a ) * d.radius,
				4 + Math.sin( t * 1.7 + d.phase ) * 2.1,
				cz + Math.sin( a ) * d.radius,
			);
			l.position.copy( _balloonTarget );
			l.intensity = 5 + Math.sin( t * 2.2 + d.phase ) * 1.8;

		}

		beamGroup.position.set( cx, 0, cz );
		for ( const beam of beams ) {

			const spec = beam.userData;
			beam.position.x = spec.x + Math.sin( t * 0.35 + spec.phase ) * 4;
			beam.position.z = spec.z + Math.cos( t * 0.25 + spec.phase ) * 6;
			beam.material.opacity = 0.04 + ( 0.03 + wideMix * 0.04 ) * ( 0.5 + 0.5 * Math.sin( t * 1.15 + spec.phase ) );

		}

		// Keep the floor bars moving slowly so they read as ambience, not a focal point.
		laserGroup.position.set( cx, 0, cz );
		for ( const lz of lasers ) {

			lz.rotation.z = lz.userData.phase + t * 0.16;
			lz.material.opacity = 0.035 + 0.035 * Math.abs( Math.sin( t * 0.7 + lz.userData.phase ) );

		}

		renderer.render( scene, camera );
		state.raf = requestAnimationFrame( animate );

	}
	animate();

	return { setTrack, dispose: () => state.dispose() };

}

function makeNeonGroundTexture() {

	const canvas = document.createElement( 'canvas' );
	canvas.width = canvas.height = 512;
	const ctx = canvas.getContext( '2d' );

	// Vibrant violet base with cyan/magenta gradient overlay
	ctx.fillStyle = '#1a0840';
	ctx.fillRect( 0, 0, 512, 512 );

	const grad = ctx.createRadialGradient( 256, 256, 0, 256, 256, 320 );
	grad.addColorStop( 0, 'rgba(255, 43, 214, 0.85)' );
	grad.addColorStop( 0.35, 'rgba(138, 59, 255, 0.55)' );
	grad.addColorStop( 0.7, 'rgba(0, 245, 255, 0.35)' );
	grad.addColorStop( 1, 'rgba(10, 0, 30, 0.0)' );
	ctx.fillStyle = grad;
	ctx.fillRect( 0, 0, 512, 512 );

	// Diagonal stripe pattern for retro arcade vibe
	ctx.save();
	ctx.translate( 256, 256 );
	ctx.rotate( Math.PI / 6 );
	ctx.translate( -256, -256 );
	for ( let i = -512; i < 1024; i += 48 ) {

		ctx.fillStyle = 'rgba(255, 213, 90, 0.06)';
		ctx.fillRect( i, 0, 24, 512 );

	}
	ctx.restore();

	// Bold grid lines in cyan + magenta
	for ( let i = 0; i < 512; i += 32 ) {

		ctx.strokeStyle = i % 64 === 0 ? 'rgba(0, 245, 255, 0.55)' : 'rgba(255, 43, 214, 0.32)';
		ctx.lineWidth = i % 64 === 0 ? 2 : 1;
		ctx.beginPath(); ctx.moveTo( i, 0 ); ctx.lineTo( i, 512 ); ctx.stroke();
		ctx.beginPath(); ctx.moveTo( 0, i ); ctx.lineTo( 512, i ); ctx.stroke();

	}

	const tex = new THREE.CanvasTexture( canvas );
	tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
	tex.repeat.set( 6, 6 );
	tex.colorSpace = THREE.SRGBColorSpace;
	return tex;

}

function spawnParticles( container, count = 28 ) {

	const colors = [ '#00f5ff', '#ff2bd6', '#8a3bff', '#34ff9d' ];
	for ( let i = 0; i < count; i ++ ) {

		const span = document.createElement( 'span' );
		const x = Math.random() * 100;
		span.style.setProperty( '--c', colors[ i % colors.length ] );
		span.style.setProperty( '--x', x + 'vw' );
		span.style.setProperty( '--drift', ( Math.random() * 8 - 4 ) + 'vw' );
		span.style.setProperty( '--dur', ( 7 + Math.random() * 7 ) + 's' );
		span.style.setProperty( '--delay', ( - Math.random() * 14 ) + 's' );
		span.style.left = x + 'vw';
		container.appendChild( span );

	}

}

// ─── Public entry ───────────────────────────────────────────────────────

export async function showHomeScreen( models = null ) {

	const params = new URLSearchParams( location.search );
	const presetMode = params.get( 'mode' );
	const presetRoom = params.get( 'room' );
	const presetName = params.get( 'name' );
	const presetTrack = params.get( 'track' );

	if ( presetMode === 'single' && ! params.has( 'mp' ) ) {

		const t = getPreset( presetTrack );
		return { mode: 'single', name: loadName( presetName ), roomId: null, trackId: t.id, cells: t.cells };

	}
	if ( ( params.has( 'mp' ) || presetMode === 'multi' ) && presetRoom ) {

		const t = getPreset( presetTrack );
		return { mode: 'multi', name: loadName( presetName ), roomId: presetRoom, trackId: t.id, cells: t.cells };

	}

	injectStyles();

	const overlay = $( 'div', { id: 'home-screen' } );
	const bgCanvas = $( 'canvas', { class: 'bg-canvas' } );
	overlay.appendChild( bgCanvas );
	overlay.appendChild( $( 'div', { class: 'bg-aurora' } ) );
	overlay.appendChild( $( 'div', { class: 'bg-vignette' } ) );
	overlay.appendChild( $( 'div', { class: 'bg-grid' } ) );
	const particles = $( 'div', { class: 'particles' } );
	overlay.appendChild( particles );
	overlay.appendChild( $( 'div', { class: 'scanline' } ) );
	spawnParticles( particles, 32 );

	const savedName = localStorage.getItem( STORAGE_NAME ) || presetName || '';
	const savedRoom = presetRoom || '';

	const nameInput = $( 'input', {
		type: 'text', placeholder: 'enter call sign',
		maxlength: 24, value: savedName,
	} );
	const roomInput = $( 'input', {
		type: 'text', placeholder: 'leave blank for public lobby',
		maxlength: 64, value: savedRoom,
	} );
	const errEl = $( 'div', { class: 'err' } );

	const single = $( 'button', { class: 'cta cta-single', type: 'button' }, [ 'Solo Time Trial' ] );
	const multi = $( 'button', { class: 'cta cta-multi', type: 'button' }, [ 'Join Online Race' ] );
	const newRoomBtn = $( 'button', { class: 'ghost-btn', type: 'button' }, [ 'New Room' ] );

	const savedTrackId = localStorage.getItem( STORAGE_TRACK ) || presetTrack || PRESET_TRACKS[ 0 ].id;
	let chosenTrackId = PRESET_TRACKS.some( ( t ) => t.id === savedTrackId ) ? savedTrackId : PRESET_TRACKS[ 0 ].id;

	const trackButtons = PRESET_TRACKS.map( ( t, idx ) => {

		const btn = $( 'button', {
			class: 'track-card' + ( t.id === chosenTrackId ? ' selected' : '' ),
			type: 'button',
			'data-track': t.id,
		}, [
			$( 'span', { class: 'num' }, [ String( idx + 1 ).padStart( 2, '0' ) ] ),
			$( 'div', { class: 't-name' }, [ t.name ] ),
			$( 'div', { class: 't-hint' }, [ t.hint ] ),
		] );
		btn.addEventListener( 'click', () => selectTrack( t.id ) );
		btn.addEventListener( 'mouseenter', () => previewTrack( t.id ) );
		btn.addEventListener( 'mouseleave', () => previewTrack( chosenTrackId ) );
		return btn;

	} );

	const trackRail = $( 'div', { class: 'track-rail' } );
	for ( const b of trackButtons ) trackRail.appendChild( b );

	const status = MP_SERVER_URL
		? $( 'div', { class: 'status' }, [ $( 'span', { class: 'dot' } ), 'Server online · ' + new URL( MP_SERVER_URL ).host ] )
		: $( 'div', { class: 'status offline' }, [ $( 'span', { class: 'dot' } ), 'Multiplayer offline · solo only' ] );

	const brandRow = $( 'div', { class: 'brand-row' }, [
		$( 'div', {}, [
			$( 'div', { class: 'brand' }, [ 'Loki\'s ', $( 'span', { class: 'accent' }, [ 'Racing' ] ), ' Pro' ] ),
			$( 'div', { class: 'brand-sub' }, [ 'select your track · throw down a lap' ] ),
		] ),
		status,
	] );

	const center = $( 'div', { class: 'center' }, [
		$( 'p', { class: 'section-label' }, [ '◢ Track Selection' ] ),
		trackRail,
		$( 'p', { class: 'section-label', style: { marginTop: '6px' } }, [ '◢ Racer Profile' ] ),
		$( 'div', { class: 'field-row' }, [
			$( 'div', { class: 'field' }, [
				$( 'span', { class: 'section-label' }, [ 'Call Sign' ] ),
				nameInput,
			] ),
			$( 'div', { class: 'field' }, [
				$( 'span', { class: 'section-label' }, [ 'Room' ] ),
				roomInput,
			] ),
			newRoomBtn,
		] ),
		errEl,
		$( 'div', { class: 'cta-row' }, [ multi, single ] ),
	] );

	const controlsBar = $( 'div', { class: 'controls-bar' } );
	controlsBar.innerHTML =
		'<span><span class="key">W A S D</span> Drive</span>' +
		'<span><span class="key">Space</span> Nitrous</span>' +
		'<span><span class="key">Shift</span> Boost</span>' +
		'<span><span class="key">H</span> Help</span>';

	const footer = $( 'div', { class: 'footer' }, [
		controlsBar,
		$( 'div', {}, [ 'v0.1 · blank room joins public lobby · share custom rooms with friends' ] ),
	] );

	const stage = $( 'div', { class: 'stage' }, [ brandRow, center, footer ] );
	overlay.appendChild( stage );
	document.body.appendChild( overlay );

	// Bring up the 3D background once we're in the DOM
	const bg = models ? createBackgroundScene( models, bgCanvas ) : null;
	let heroIdx = Math.floor( Math.random() * HERO_VEHICLES.length );

	function previewTrack( id ) {

		if ( ! bg ) return;
		const t = getPreset( id );
		bg.setTrack( t.cells, HERO_VEHICLES[ heroIdx ] );

	}

	function selectTrack( id ) {

		chosenTrackId = id;
		heroIdx = ( heroIdx + 1 ) % HERO_VEHICLES.length;
		for ( const b of trackButtons ) b.classList.toggle( 'selected', b.dataset.track === id );
		previewTrack( id );

	}

	previewTrack( chosenTrackId );

	newRoomBtn.addEventListener( 'click', () => {

		roomInput.value = genRoomId();
		roomInput.focus();

	} );

	if ( ! MP_SERVER_URL ) {

		multi.disabled = true;

	}

	return await new Promise( ( resolve ) => {

		const finish = ( mode ) => {

			const name = nameInput.value.trim() || `racer-${ Math.floor( Math.random() * 999 ) }`;
			const roomId = mode === 'multi' ? ( roomInput.value.trim() || 'default' ) : null;
			const track = getPreset( chosenTrackId );
			localStorage.setItem( STORAGE_NAME, name );
			localStorage.setItem( STORAGE_TRACK, track.id );
			if ( roomId && roomId !== 'default' ) {

				localStorage.setItem( STORAGE_ROOM, roomId );

			} else {

				localStorage.removeItem( STORAGE_ROOM );

			}
			if ( bg ) bg.dispose();
			overlay.remove();
			resolve( { mode, name, roomId, trackId: track.id, cells: track.cells } );

		};

		single.addEventListener( 'click', () => finish( 'single' ) );
		multi.addEventListener( 'click', () => {

			if ( ! MP_SERVER_URL ) {

				errEl.textContent = 'no multiplayer server configured';
				return;

			}
			finish( 'multi' );

		} );

		nameInput.focus();

	} );

}
